import { useI18n } from '../i18n/useI18n'

/**
 * La marque SEGISEN : l'immeuble, puis le nom.
 *
 * Dessine en SVG et non charge comme image, pour trois raisons : rien
 * de plus a telecharger sur la connexion de Mbour, une nettete
 * parfaite a toute taille, et des couleurs qui suivent le fond clair
 * ou sombre au lieu d'etre figees dans un fichier.
 *
 * Le motif reprend le logo : l'immeuble vu de coin, une tour haute
 * dont on voit deux faces, et une aile plus basse posee devant a
 * gauche. Les faces qui s'eloignent sont plus sombres, les aretes du
 * haut fuient vers les cotes : c'est ce qui donne le relief sans
 * degrade ni ombre portee, donc sans un octet de plus.
 *
 * La marque est plus large que haute, comme le logo : `size` fixe la
 * hauteur, la largeur suit.
 */
export function LogoMark({
  size = 56,
  onDark = false,
}: {
  size?: number
  /** Sur fond sombre, les volumes passent en creme. */
  onDark?: boolean
}) {
  /** Face principale, celle qui nous fait face. */
  const corps = onDark ? '#faf6f0' : '#2f3336'
  /** Face qui s'eloigne : toujours un cran plus sombre que `corps`. */
  const fuite = onDark ? '#ded2c4' : '#22262a'
  /** Lames verticales : elles doivent trancher sur `corps`. */
  const lame = onDark ? '#3a2317' : '#faf6f0'
  /**
   * Lames posees sur le terracotta. Elles restent cremes dans les deux
   * cas : le terracotta est un ton moyen, le creme tranche dessus sur
   * fond clair comme sur fond sombre, et c'est ainsi sur le logo.
   */
  const lameTerre = '#faf6f0'
  /** Vitrages, le ton le plus profond du dessin. */
  const verre = onDark ? '#b9a996' : '#15181a'
  const terre = '#b0552f'

  return (
    <svg
      width={size * 1.2}
      height={size}
      viewBox="0 0 120 100"
      role="img"
      aria-label="SEGISEN"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* Le sol, dessine en premier : l'immeuble se pose dessus. */}
      <polygon points="2,96 116,89 116,92 2,99" fill={corps} opacity="0.45" />

      {/* ------- La tour, vue de coin : arete verticale a x=72 ------- */}

      {/* Face gauche, celle des vitrages et des balcons. */}
      <polygon points="35,19 72,1 72,96 35,94" fill={corps} />
      <polygon points="46,18.6 60,11.8 60,93 46,92" fill={verre} />
      {/* Balcons : trois lignes qui suivent la fuite du toit. */}
      <polygon points="46,40 60,33.2 60,36.2 46,43" fill={lame} />
      <polygon points="46,55 60,48.2 60,51.2 46,58" fill={lame} />
      <polygon points="46,70 60,63.2 60,66.2 46,73" fill={lame} />
      {/* Deux lames avant l'arete. */}
      <polygon points="63,5.4 66,3.9 66,95.7 63,95.5" fill={lame} />
      <polygon points="68,3.0 71,1.5 71,95.8 68,95.7" fill={lame} />

      {/* Face droite : le grand panneau terracotta du logo. */}
      <polygon points="72,1 110,27 110,92 72,96" fill={terre} />
      <polygon points="78,5.1 80.5,6.8 80.5,95.1 78,95.4" fill={lameTerre} />
      <polygon points="86,10.6 88.5,12.3 88.5,94.3 86,94.5" fill={lameTerre} />
      <polygon points="94,16.0 96.5,17.8 96.5,93.4 94,93.7" fill={lameTerre} />
      {/* L'arete droite, plus sombre : la tour se referme. */}
      <polygon points="102,21.5 110,27 110,92 102,92.8" fill={fuite} />

      {/* ------- L'aile basse, posee devant : elle masque la tour ------- */}

      {/* Face gauche, qui s'eloigne. */}
      <polygon points="4,63 30,49 30,96 4,95" fill={fuite} />
      <polygon points="12,58.7 15,57.1 15,95.3 12,95.3" fill={lame} />
      <polygon points="20,54.4 23,52.8 23,95.6 20,95.5" fill={lame} />

      {/* Face droite, terracotta comme sur le logo. */}
      <polygon points="30,49 46,57 46,96 30,96" fill={terre} />
      <polygon points="34,51 36.5,52.25 36.5,96 34,96" fill={lameTerre} />
      <polygon points="40,54 42.5,55.25 42.5,96 40,96" fill={lameTerre} />
    </svg>
  )
}

/**
 * La marque complete : l'immeuble, le filet terracotta, le nom et la
 * raison sociale. Utilisee sur les deux ecrans d'entree, connexion et
 * choix de l'annee.
 *
 * Le nom reste du texte et non un trace : il se lit a la voix haute
 * par les lecteurs d'ecran, il se cherche dans la page, et il garde la
 * police de l'application.
 */
export function Logo({ onDark = false }: { onDark?: boolean }) {
  const { t } = useI18n()

  return (
    <div className={`logo ${onDark ? 'logo--dark' : ''}`}>
      {/* L'immeuble est pose sur une plaque creme et garde ses vraies
          couleurs. Sans la plaque, ses panneaux terracotta se
          confondraient avec le fond terracotta de l'application et la
          marque perdrait ses deux tons. C'est aussi, exactement,
          l'icone posee sur l'ecran d'accueil du telephone. */}
      <span className="logo__plaque">
        <LogoMark size={40} />
      </span>
      {/* Le filet vertical qui separe la marque du nom, comme sur le
          logo. Purement decoratif, donc invisible aux lecteurs
          d'ecran. */}
      <span className="logo__rule" aria-hidden="true" />
      <div className="logo__text">
        {/* Nom de la societe : jamais traduit. */}
        <span className="logo__name">{t('app_name')}</span>
        <span className="logo__tagline">{t('app_legal_name')}</span>
      </div>
    </div>
  )
}
